import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SeoHead from "@/components/feature/SeoHead";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MangaCard from "@/components/feature/MangaCard";
import SectionHeader from "@/components/feature/SectionHeader";
import { mangaWorks } from "@/mocks/manga";
import { fitnessChapters } from "@/mocks/chapters";

const DEFAULT_VISIBLE = 48;

export default function MangaDetailPage() {
  const { mangaId } = useParams();
  const work = mangaWorks.find((w) => w.id === mangaId);

  const [reverse, setReverse] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [favorited, setFavorited] = useState(false);
  const [copied, setCopied] = useState(false);

  const chapterNumbers = useMemo(
    () => Array.from({ length: work?.totalChapters ?? 0 }, (_, i) => i + 1),
    [work],
  );
  const ordered = reverse ? [...chapterNumbers].reverse() : chapterNumbers;
  const visibleChapters = showAll ? ordered : ordered.slice(0, DEFAULT_VISIBLE);

  if (!work) {
    return (
      <div className="mx-auto flex w-full max-w-7xl flex-col items-center px-4 py-20 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-3xl text-foreground-500">
          <i className="ri-emotion-sad-line"></i>
        </span>
        <h1 className="mt-5 font-heading text-xl font-black text-foreground-950">没有找到这部漫画</h1>
        <p className="mt-2 text-sm text-foreground-600">作品可能已被下架，去看看其他热门漫画吧</p>
        <Link
          to="/category/all"
          className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-book-2-line"></i>
          浏览漫画大全
        </Link>
      </div>
    );
  }

  const chapterTitleOf = (num: number) => {
    const chapter = fitnessChapters.find((c) => c.index === num);
    return chapter ? `第${num}话 ${chapter.title}` : `第${num}话`;
  };

  const similarWorks = mangaWorks
    .filter((w) => w.category === work.category && w.id !== work.id)
    .slice(0, 6);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(`https://workoutpro0.github.io/manga/${work.id}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const jsonLd = [
    {
      "@type": "ComicStory",
      name: `《${work.title}》漫画`,
      url: `https://workoutpro0.github.io/manga/${work.id}`,
      image: work.cover,
      description: work.description,
      inLanguage: "zh-CN",
      genre: work.categoryName,
      author: { "@type": "Person", name: work.author },
      datePublished: `${work.year}-01-01`,
      isPartOf: { "@id": "https://workoutpro0.github.io/#website" },
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: work.rating,
        bestRating: 10,
        worstRating: 1,
        ratingCount: work.views.replace(/[万\.]/g, "") + "000",
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://workoutpro0.github.io/" },
        {
          "@type": "ListItem",
          position: 2,
          name: `${work.categoryName}漫画`,
          item: `https://workoutpro0.github.io/category/${work.category}`,
        },
        { "@type": "ListItem", position: 3, name: `《${work.title}》漫画` },
      ],
    },
  ];

  return (
    <>
      <SeoHead
        title={`《${work.title}》漫画-${work.categoryName}漫画免费阅读-43423漫画网`}
        description={`在43423漫画网免费阅读《${work.title}》漫画全集，${work.author}创作${work.categoryName}题材作品，已更新至${work.latestChapter}，支持高清在线阅读。`}
        path={`/manga/${work.id}`}
        type="book"
        image={work.cover}
        jsonLd={jsonLd}
      />

      <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: `${work.categoryName}漫画`, to: `/category/${work.category}` },
            { label: `《${work.title}》漫画` },
          ]}
        />

        <section className="mt-5 overflow-hidden rounded-lg border border-background-200/70 bg-background-100 p-4 md:p-6">
          <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
            <div className="w-full shrink-0 lg:w-[260px]">
              <div className="relative aspect-[3/4] w-full overflow-hidden rounded-lg bg-background-200">
                <img
                  src={work.cover}
                  alt={`《${work.title}》漫画封面`}
                  title={`《${work.title}》漫画免费阅读`}
                  className="h-full w-full object-cover object-top"
                />
                <span
                  className={`absolute left-3 top-3 rounded-full px-3 py-1 text-xs font-bold text-background-50 ${
                    work.status === "已完结" ? "bg-secondary-500" : "bg-accent-500"
                  }`}
                >
                  {work.status}
                </span>
              </div>
              <div className="mt-4 flex flex-col gap-2">
                <Link
                  to={`/reader/${work.id}/1`}
                  className="inline-flex w-full items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-book-open-line mr-2 text-base"></i>
                  开始阅读
                </Link>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setFavorited((v) => !v)}
                    className={`inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border px-3 py-2.5 text-xs font-bold transition-colors ${
                      favorited
                        ? "border-primary-300 bg-primary-50 text-primary-600"
                        : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                    }`}
                  >
                    <i className={`${favorited ? "ri-heart-fill" : "ri-heart-line"} text-sm`}></i>
                    {favorited ? "已收藏" : "收藏"}
                  </button>
                  <button
                    type="button"
                    onClick={handleShare}
                    className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-xs font-bold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    <i className="ri-share-line text-sm"></i>
                    {copied ? "已复制" : "分享"}
                  </button>
                </div>
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <h1 className="font-heading text-2xl font-black leading-snug text-foreground-950 md:text-3xl">
                《{work.title}》漫画
              </h1>

              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-600">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-star-line text-sm text-primary-500"></i>
                  作者：{work.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-price-tag-3-line text-sm text-primary-500"></i>
                  分类：{work.categoryName}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line text-sm text-primary-500"></i>
                  更新：{work.updateTime}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line text-sm text-primary-500"></i>
                  人气：{work.views}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-heart-line text-sm text-primary-500"></i>
                  收藏：{work.favorites}
                </span>
              </div>

              <div className="mt-4 flex items-center gap-3">
                <span className="flex items-center gap-0.5 text-lg text-accent-500" aria-label={`评分${work.rating}`}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i key={star} className={star <= Math.round(work.rating / 2) ? "ri-star-fill" : "ri-star-line"}></i>
                  ))}
                </span>
                <strong className="font-comic text-2xl font-bold text-accent-700">{work.rating.toFixed(1)}</strong>
                <span className="text-xs text-foreground-600">/ 10 分</span>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {work.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/category/all?q=${encodeURIComponent(tag)}`}
                    className="whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs font-bold text-secondary-900 transition-colors hover:bg-secondary-500 hover:text-background-50"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <div className="mt-5 rounded-lg border border-background-200/70 bg-background-50 p-4">
                <h2 className="flex items-center gap-2 text-sm font-black text-foreground-950">
                  <i className="ri-file-text-line text-primary-500"></i>
                  漫画简介
                </h2>
                <p className="mt-3 text-xs leading-relaxed text-foreground-700 md:text-sm">{work.description}</p>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                  { icon: "ri-book-2-line", label: "全本话数", value: `${work.totalChapters}话` },
                  { icon: "ri-refresh-line", label: "最近更新", value: work.latestChapter.replace(/^第\d+话/, "") },
                  { icon: "ri-calendar-check-line", label: "连载年份", value: `${work.year}年` },
                  { icon: "ri-fire-line", label: "热度排名", value: `第${work.hot}名` },
                ].map((item) => (
                  <div key={item.label} className="rounded-lg border border-background-200/70 bg-background-50 p-3 text-center">
                    <i className={`${item.icon} text-base text-primary-500`}></i>
                    <p className="mt-1.5 text-[11px] text-foreground-600">{item.label}</p>
                    <p className="mt-0.5 truncate text-xs font-bold text-foreground-950">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="mt-8" aria-label="章节列表">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2.5">
              <span className="h-5 w-1 rounded-full bg-primary-500"></span>
              <h2 className="font-heading text-xl font-black text-foreground-950">章节列表</h2>
              <span className="rounded-full bg-accent-100 px-2.5 py-1 text-xs font-bold text-accent-700">
                共{work.totalChapters}话
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex rounded-full bg-background-100 p-1" role="tablist" aria-label="章节排序">
                {(
                  [
                    { key: false, label: "正序" },
                    { key: true, label: "倒序" },
                  ] as const
                ).map((item) => (
                  <button
                    key={item.label}
                    type="button"
                    role="tab"
                    aria-selected={reverse === item.key}
                    onClick={() => setReverse(item.key)}
                    className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${
                      reverse === item.key
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-700 hover:text-primary-600"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setShowAll((v) => !v)}
                className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-200 bg-background-100 px-3.5 py-2 text-xs font-bold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className={`${showAll ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-sm`}></i>
                {showAll ? "收起" : "展开全部"}
              </button>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {visibleChapters.map((num) => (
              <Link
                key={num}
                to={`/reader/${work.id}/${num}`}
                title={chapterTitleOf(num)}
                className="flex items-center justify-center whitespace-nowrap rounded-md border border-background-200 bg-background-100 px-2 py-2.5 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
              >
                {chapterTitleOf(num)}
              </Link>
            ))}
          </div>
          {!showAll && work.totalChapters > DEFAULT_VISIBLE && (
            <p className="mt-3 text-center text-[11px] text-foreground-500">
              仅显示最近{DEFAULT_VISIBLE}话，点击“展开全部”查看完整目录
            </p>
          )}
        </section>

        {similarWorks.length > 0 && (
          <section className="mt-10" aria-label="同类推荐">
            <SectionHeader
              title={`同类${work.categoryName}漫画推荐`}
              subtitle="喜欢这部作品的话，这些可能也合你胃口"
              moreLink={`/category/${work.category}`}
              moreText="同类型"
            />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 md:gap-4">
              {similarWorks.map((item) => (
                <MangaCard key={item.id} work={item} />
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}